import React, { useState, useEffect, useRef } from "react";

const DEFAULT_PHASES = [
  { name: "Inhale", duration: 4 },
  { name: "Hold",   duration: 4 },
  { name: "Exhale", duration: 4 },
  { name: "Hold",   duration: 4 },
];
const DEFAULT_CYCLES = 3;

// Phase → orb CSS state
// Brand colors: orange #CB5A3C (203,90,60) | purple #5B3FD6 (91,63,214) | indigo #7C66DE (124,102,222)
const PHASE_STYLES = {
  Inhale: {
    orbSize: 200,
    orbBg: "radial-gradient(circle, rgba(40,10,80,0.85) 30%, rgba(203,90,60,0.65) 65%, rgba(40,10,80,0.9))",
    boxShadow: "0 0 40px rgba(203,90,60,0.5), 0 0 80px rgba(203,90,60,0.25), inset 0 0 30px rgba(203,90,60,0.15)",
    ringColors: ["rgba(203,90,60,0.5)", "rgba(203,90,60,0.3)", "rgba(203,90,60,0.15)"],
  },
  Hold: {
    orbSize: 200,
    orbBg: "radial-gradient(circle, rgba(30,15,60,0.9) 30%, rgba(178,152,226,0.45) 65%, rgba(30,15,60,0.95))",
    boxShadow: "0 0 35px rgba(178,152,226,0.4), 0 0 70px rgba(178,152,226,0.18), inset 0 0 25px rgba(178,152,226,0.12)",
    ringColors: ["rgba(178,152,226,0.4)", "rgba(178,152,226,0.22)", "rgba(178,152,226,0.1)"],
  },
  Exhale: {
    orbSize: 140,
    orbBg: "radial-gradient(circle at 40% 35%, rgba(124,102,222,0.5), rgba(91,63,214,0.25) 60%, rgba(20,10,50,0.85))",
    boxShadow: "0 0 30px rgba(124,102,222,0.4), 0 0 60px rgba(124,102,222,0.18), inset 0 0 20px rgba(124,102,222,0.12)",
    ringColors: ["rgba(124,102,222,0.4)", "rgba(124,102,222,0.22)", "rgba(124,102,222,0.1)"],
  },
};

export default function BreathingTemplate({ config = {}, onProgress, onComplete }) {
  const phases      = config.phases?.length ? config.phases : DEFAULT_PHASES;
  const totalCycles = config.cycles || DEFAULT_CYCLES;

  const [phaseIndex, setPhaseIndex] = useState(0);
  const [cycle, setCycle]           = useState(1);
  const [timeLeft, setTimeLeft]     = useState(phases[0].duration);
  const [isRunning, setIsRunning]   = useState(false);

  const completedRef = useRef(false);

  const currentPhase = phases[phaseIndex];
  const phaseName    = currentPhase?.name || "Breathe";
  const style        = PHASE_STYLES[phaseName] || PHASE_STYLES.Hold;

  // Report progress to shell
  useEffect(() => {
    if (!onProgress) return;
    const totalPhases = phases.length * totalCycles;
    const donePhases  = (cycle - 1) * phases.length + phaseIndex;
    onProgress(donePhases / totalPhases, `Cycle ${cycle} of ${totalCycles}`);
  }, [cycle, phaseIndex]); // eslint-disable-line

  useEffect(() => {
    if (!isRunning) return;

    const interval = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          const nextPhase = (phaseIndex + 1) % phases.length;
          if (nextPhase === 0) {
            if (cycle >= totalCycles) {
              setIsRunning(false);
              if (!completedRef.current) {
                completedRef.current = true;
                onComplete?.();
              }
              return 0;
            }
            setCycle((c) => c + 1);
          }
          setPhaseIndex(nextPhase);
          return phases[nextPhase].duration;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning, phaseIndex, cycle]); // eslint-disable-line

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-6">
      {/* Orb — dark container, white text inside stays white */}
      <div className="tw-relative tw-flex tw-items-center tw-justify-center" style={{ width: 260, height: 260 }}>
        {/* Glow rings */}
        {[190, 220, 250].map((base, i) => (
          <div
            key={i}
            className="tw-absolute tw-rounded-full tw-border tw-transition-all tw-duration-1000"
            style={{
              width: base,
              height: base,
              borderColor: style.ringColors[i],
            }}
          />
        ))}

        {/* Core orb */}
        <div
          className="tw-rounded-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-relative tw-z-10 tw-transition-all tw-duration-1000"
          style={{
            width: style.orbSize,
            height: style.orbSize,
            background: style.orbBg,
            boxShadow: style.boxShadow,
          }}
        >
          <p className="tw-text-lg tw-font-light tw-tracking-[2px] tw-text-white">{phaseName}</p>
          <p className="tw-text-4xl tw-font-extralight tw-text-white tw-leading-none tw-mt-0.5">{timeLeft}</p>
        </div>
      </div>

      <p className="tw-text-sm tw-tracking-[1px]" style={{ color: "rgba(255,255,255,0.45)" }}>
        Cycle {cycle} of {totalCycles}
      </p>

      <button
        className="tw-px-8 tw-py-2 tw-rounded-3xl tw-text-white tw-font-medium tw-transition-all tw-duration-200 hover:tw-scale-105"
        style={{
          background: "#5B3FD6",
          border: "none",
        }}
        onClick={() => setIsRunning((r) => !r)}
      >
        {isRunning ? "Pause" : "Start"}
      </button>
    </div>
  );
}
